import {
  EApplicantType,
  EConsignoSignatureStatus,
  ECreditApplicationStatus,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EExternalStep,
  EExternalStepStatus,
  ELoanPurpose,
  EProvince,
} from '@if/core/types'
import { RequiredDocument } from '@src/types'
import { FilteredWorksheet } from './FilteredWorksheet'

export interface RequiredExternalStep {
  id: string
  applicantType: EApplicantType
  externalServiceId: string
  externalStepId: EExternalStep
  status: EExternalStepStatus
  extraProperties: Record<string, string>
}

export interface CreditApplicationDocument {
  applicantType: EApplicantType
  status: EDocumentStatus
  typeId: EDocumentType
  refusalReason: string | null
  subKey: string | null
}

export interface PrequalificationDecision {
  decision: EDecision
  minInterestRate: number
  maxInterestRate: number
  maxLoanAmount: number
  maxLoanTerm: number
}
export interface NormsDecision {
  decision: string
  interestRate: number
  maxPmtAmount: number
  maxTermDuration: number
  maxAmountFinanced: number
  lenderFeeRate: number
}

export interface FinalDecision {
  decision: EDecision
  interestRate: number
  maxPmtAmount: number
  maxTermDuration: number
  maxAmountFinanced: number
}

export interface MerchantPaymentPlan {
  merchantFeeRate: number
  borrowerFeeRate: number
  reducedInterestRate: number
  interestRate: number
  reducedRateDurationInMonths: number
  loanTerm: number
}

export interface FilteredApplicant {
  firstName: string
  lastName: string
  middleName: string
  province: EProvince
  cellPhone: string
  homePhone: string
  age: number
  hasSin: boolean
  hasCreditReport: boolean
  hasLockedCreditReport: boolean
  isCreditTwoYearsOrLess: boolean
  areIncomesConfirmed: boolean
  areFlinksIncomeMatchingDeclared: boolean | null
  flinksHasEmploymentOrSocialInsuranceIncome: boolean | null
  canContinueWithComputedIncome: boolean | null
  hasSkippedIncomeValidation: boolean
  computedIncome: number | null
}

export enum EContractStatus {
  Ready = 'ready',
  Sending = 'sending',
  Declined = 'declined',
  NotReady = 'notready',
  UnderReview = 'review',
  Completed = 'completed',
}

export interface Contract {
  status: EContractStatus
  declinedReason: string | null
}

export enum EOriginSystemId {
  Backoffice = 'backoffice',
  MerchantDashboard = 'merchdb',
  Subscription = 'ifinancesub',
  B2c = 'b2csub',
  Autologiq = 'autologiq',
}

export interface Signature {
  status: EConsignoSignatureStatus
  isApplicant: boolean
  declinedReason: string
}

export interface FilteredCreditApplication {
  id: string
  referenceNumber: number
  status: ECreditApplicationStatus
  applicant: FilteredApplicant
  coapplicant: FilteredApplicant | null
  loanPurposeId: ELoanPurpose
  requiredDocuments: RequiredDocument[]
  documents: CreditApplicationDocument[]
  prequalificationDecision: PrequalificationDecision
  normsDecision: NormsDecision
  finalDecision: FinalDecision
  merchantPaymentPlan: MerchantPaymentPlan | null
  requestedLoanAmount: number
  worksheet: FilteredWorksheet | null
  contract: Contract
  merchantId: string
  merchantPaymentPlanId: string | null
  allIncomesConfirmed: boolean
  requiredExternalSteps: RequiredExternalStep[]
  completedVerifyAccountName: boolean
  loanCreated: boolean
  applicantWantsToCancel: boolean
  consentSoftHit: boolean
  consentHardHit: boolean
  updatedOn: Date
  cvtNumber: number | null
  originSystemId: EOriginSystemId
  expiresOn: Date
  hardHitReportReceivedOn: Date
  allTaskCompletedForCVT: boolean
  signers: Signature[]
}
